import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Settings, LogOut, Menu, X, Linkedin, Instagram } from 'lucide-react';
import { useState } from 'react';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_c03b2305-10b0-43c0-8499-c191377bc941/artifacts/02yyq1u7_EconomicClubofSa.png';

export default function Header() {
  const { user, signOut, isAdmin } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleSignOut = async () => {
    await signOut();
    navigate('/auth');
  };

  const navItems = [
    { to: '/', label: 'Events' },
    { to: '/members', label: 'Members' },
    { to: '/dinner-club', label: 'Dinner Club' },
    { to: '/book-club', label: 'Book Club' },
    { to: '/about', label: 'About ECSA' },
  ];

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-gradient-to-b from-black/90 to-black/70 backdrop-blur-xl border-b border-brand-gold/20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          {/* Logo */}
          <div 
            className="flex items-center cursor-pointer group" 
            onClick={() => navigate('/')}
          >
            <img 
              src={LOGO_URL} 
              alt="Economic Club of San Antonio" 
              className="h-14 w-auto transition-transform group-hover:scale-105"
            />
          </div>

          {/* Social Links */}
          <div className="hidden sm:flex items-center gap-3 ml-4">
            <a 
              href="https://www.linkedin.com/company/economic-club-of-san-antonio" 
              target="_blank" 
              rel="noopener noreferrer"
              className="p-2 rounded-lg bg-white/5 border border-white/10 hover:bg-brand-gold/20 hover:border-brand-gold/30 transition-all duration-300"
              title="LinkedIn"
            >
              <Linkedin size={18} className="text-gray-400 hover:text-brand-gold" />
            </a>
            <a 
              href="https://www.instagram.com/econclubsa" 
              target="_blank" 
              rel="noopener noreferrer"
              className="p-2 rounded-lg bg-white/5 border border-white/10 hover:bg-brand-gold/20 hover:border-brand-gold/30 transition-all duration-300"
              title="Instagram"
            >
              <Instagram size={18} className="text-gray-400 hover:text-brand-gold" />
            </a>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-1">
            {navItems.map(({ to, label }) => (
              <button
                key={to}
                onClick={() => navigate(to)}
                className={`relative px-4 py-2 text-sm font-medium transition-all duration-300 rounded-lg ${
                  location.pathname === to 
                    ? 'text-brand-gold bg-brand-gold/10' 
                    : 'text-gray-300 hover:text-white hover:bg-white/5'
                }`}
              >
                {label}
                {location.pathname === to && (
                  <span className="absolute bottom-0 left-1/2 -translate-x-1/2 w-1 h-1 bg-brand-gold rounded-full" />
                )}
              </button>
            ))}
          </nav>

          {/* Auth Buttons */}
          <div className="flex items-center space-x-3">
            {!user ? (
              <>
                <button
                  onClick={() => navigate('/auth')}
                  className="hidden sm:block px-5 py-2.5 text-sm font-semibold text-brand-gold border border-brand-gold/50 rounded-xl hover:bg-brand-gold/10 hover:border-brand-gold transition-all duration-300"
                  data-testid="sign-in-btn"
                >
                  Sign In
                </button>
                <button
                  onClick={() => navigate('/application')}
                  className="px-5 py-2.5 text-sm font-semibold bg-gradient-to-r from-brand-gold to-yellow-500 text-black rounded-xl hover:shadow-lg hover:shadow-brand-gold/25 transition-all duration-300 transform hover:-translate-y-0.5"
                  data-testid="apply-btn"
                >
                  Apply Now
                </button>
              </>
            ) : (
              <>
                {isAdmin && (
                  <button
                    onClick={() => navigate('/admin')}
                    className="p-2.5 rounded-xl bg-gradient-to-r from-brand-gold/20 to-yellow-500/20 border border-brand-gold/30 hover:border-brand-gold/50 transition-all duration-300"
                    data-testid="admin-btn"
                  >
                    <Settings size={20} className="text-brand-gold" />
                  </button>
                )}
                <button
                  onClick={handleSignOut}
                  className="p-2.5 rounded-xl bg-white/5 border border-white/10 hover:bg-white/10 hover:border-white/20 transition-all duration-300"
                  data-testid="logout-btn"
                >
                  <LogOut size={20} className="text-gray-400" />
                </button>
              </>
            )}

            {/* Mobile Menu Button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden p-2 rounded-lg hover:bg-white/10 transition-colors"
            >
              {mobileMenuOpen ? <X size={24} className="text-brand-gold" /> : <Menu size={24} className="text-brand-gold" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Navigation */}
      {mobileMenuOpen && (
        <div className="md:hidden absolute top-full left-0 right-0 bg-black/95 backdrop-blur-xl border-b border-brand-gold/20">
          <div className="px-4 py-4 space-y-2">
            {navItems.map(({ to, label }) => (
              <button
                key={to}
                onClick={() => { navigate(to); setMobileMenuOpen(false); }}
                className={`block w-full text-left px-4 py-3 rounded-xl font-medium transition-colors ${
                  location.pathname === to 
                    ? 'text-brand-gold bg-brand-gold/10' 
                    : 'text-gray-300 hover:text-white hover:bg-white/5'
                }`}
              >
                {label}
              </button>
            ))}
            {!user && (
              <button
                onClick={() => { navigate('/auth'); setMobileMenuOpen(false); }}
                className="block w-full text-left px-4 py-3 rounded-xl font-medium text-brand-gold hover:bg-brand-gold/10 transition-colors sm:hidden"
              >
                Sign In
              </button>
            )}
            {/* Mobile Social Links */}
            <div className="flex items-center gap-3 px-4 pt-4 border-t border-white/10">
              <a 
                href="https://www.linkedin.com/company/economic-club-of-san-antonio" 
                target="_blank" 
                rel="noopener noreferrer"
                className="p-2 rounded-lg bg-white/5 border border-white/10 hover:bg-brand-gold/20 transition-colors"
              >
                <Linkedin size={20} className="text-gray-400" />
              </a>
              <a 
                href="https://www.instagram.com/econclubsa" 
                target="_blank" 
                rel="noopener noreferrer"
                className="p-2 rounded-lg bg-white/5 border border-white/10 hover:bg-brand-gold/20 transition-colors"
              >
                <Instagram size={20} className="text-gray-400" />
              </a>
              <span className="text-gray-500 text-sm ml-2">Follow us</span>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
